import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Landmark, RefreshCw, Link2, EyeOff, CheckCircle2 } from 'lucide-react';
import api from '../lib/api';
import { toast } from 'sonner';

const fmt = (v) => new Intl.NumberFormat('nl-BE', { style: 'currency', currency: 'EUR' }).format(v || 0);
const fmtDate = (d) => d ? new Date(d).toLocaleDateString('nl-BE') : '-';

const STATUS_BADGE = {
  matched: { label: 'Gekoppeld', cls: 'bg-green-500/15 text-green-600 border-green-500/30' },
  suggested: { label: 'Suggestie', cls: 'bg-blue-500/15 text-blue-600 border-blue-500/30' },
  unmatched: { label: 'Geen match', cls: 'bg-orange-500/15 text-orange-600 border-orange-500/30' },
  ignored: { label: 'Genegeerd', cls: 'bg-muted text-muted-foreground' },
  outgoing: { label: 'Uitgaand', cls: 'bg-muted text-muted-foreground' },
  pending: { label: 'Nieuw', cls: 'bg-muted text-muted-foreground' },
};

const FILTERS = [
  { key: '', label: 'Alles' },
  { key: 'suggested', label: 'Suggesties' },
  { key: 'unmatched', label: 'Geen match' },
  { key: 'matched', label: 'Gekoppeld' },
];

export const BankTransacties = () => {
  const [status, setStatus] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [filter, setFilter] = useState('');
  const [syncing, setSyncing] = useState(false);
  const [matchTx, setMatchTx] = useState(null);
  const [openFacturen, setOpenFacturen] = useState([]);

  const load = useCallback(async (f = filter) => {
    try {
      const [s, t] = await Promise.all([
        api.get('/ponto/status'),
        api.get(`/ponto/transactions${f ? `?status=${f}` : ''}`),
      ]);
      setStatus(s.data);
      setTransactions(t.data);
    } catch {
      toast.error('Kon bankgegevens niet laden');
    }
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  const doSync = async () => {
    setSyncing(true);
    try {
      const res = await api.post('/ponto/sync');
      toast.success(`${res.data.new_transactions} nieuwe transacties · ${res.data.matched} automatisch gekoppeld · ${res.data.suggested} suggesties`);
      await load();
    } catch {
      toast.error('Synchronisatie mislukt');
    } finally {
      setSyncing(false);
    }
  };

  const doMatch = async (tx, factuurId, collection) => {
    try {
      const res = await api.post(`/ponto/transactions/${tx.ponto_id}/match`, { factuur_id: factuurId, collection });
      toast.success(`Gekoppeld aan factuur ${res.data.factuur_number}`);
      setMatchTx(null);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Koppelen mislukt');
    }
  };

  const doIgnore = async (tx) => {
    try {
      await api.post(`/ponto/transactions/${tx.ponto_id}/ignore`);
      await load();
    } catch {
      toast.error('Negeren mislukt');
    }
  };

  const openMatchDialog = async (tx) => {
    setMatchTx(tx);
    try {
      const res = await api.get('/ponto/open-facturen');
      setOpenFacturen(res.data);
    } catch {
      toast.error('Kon openstaande facturen niet laden');
    }
  };

  if (status && !status.configured) {
    return <Card><CardContent className="pt-6 text-muted-foreground">Ponto is nog niet geconfigureerd.</CardContent></Card>;
  }

  return (
    <div className="space-y-4" data-testid="bank-transacties">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {(status?.accounts || []).map((a) => (
          <Card key={a.id} data-testid="bank-account-card">
            <CardContent className="pt-6 flex items-start justify-between">
              <div>
                <div className="flex items-center gap-2 text-sm text-muted-foreground"><Landmark className="w-4 h-4" /> {a.description || 'Bankrekening'}</div>
                <p className="font-mono text-sm mt-1">{a.iban}</p>
                <p className="text-xs text-muted-foreground mt-1">Bank laatst gesynchroniseerd: {a.synchronized_at ? new Date(a.synchronized_at).toLocaleString('nl-BE') : '-'}</p>
              </div>
              <Badge variant="outline" className="bg-green-500/10 text-green-600 border-green-500/30">Verbonden</Badge>
            </CardContent>
          </Card>
        ))}
        {status?.error && <Card><CardContent className="pt-6 text-red-500 text-sm">{status.error}</CardContent></Card>}
      </div>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0">
          <CardTitle className="text-base">Banktransacties</CardTitle>
          <Button size="sm" onClick={doSync} disabled={syncing} data-testid="ponto-sync-btn">
            <RefreshCw className={`w-4 h-4 mr-2 ${syncing ? 'animate-spin' : ''}`} />
            {syncing ? 'Bezig...' : 'Nu synchroniseren'}
          </Button>
        </CardHeader>
        <CardContent>
          <div className="flex gap-2 mb-4 flex-wrap">
            {FILTERS.map((f) => (
              <Button key={f.key} size="sm" variant={filter === f.key ? 'default' : 'outline'}
                onClick={() => setFilter(f.key)} data-testid={`ponto-filter-${f.key || 'all'}`}>
                {f.label}
                {f.key && status?.counts?.[f.key] ? ` (${status.counts[f.key]})` : ''}
              </Button>
            ))}
          </div>
          {transactions.length === 0 && (
            <p className="text-sm text-muted-foreground py-6 text-center">Geen transacties. Klik op "Nu synchroniseren" om transacties op te halen.</p>
          )}
          <div className="divide-y divide-border/50">
            {transactions.filter((tx) => (tx.amount || 0) > 0).map((tx) => {
              const badge = STATUS_BADGE[tx.match_status] || STATUS_BADGE.pending;
              const incoming = (tx.amount || 0) > 0;
              return (
                <div key={tx.ponto_id} className="py-3 flex flex-wrap items-center gap-3" data-testid="ponto-tx-row">
                  <div className="flex-1 min-w-[220px]">
                    <p className="text-sm font-medium truncate">{tx.counterpart_name || tx.description || 'Onbekend'}</p>
                    <p className="text-xs text-muted-foreground truncate">{tx.remittance || tx.additional_info || '-'}</p>
                    {tx.match_status === 'matched' && (
                      <p className="text-xs text-green-600 flex items-center gap-1 mt-0.5">
                        <CheckCircle2 className="w-3 h-3" /> Factuur {tx.matched_factuur_number} {tx.matched_auto ? '(automatisch)' : '(handmatig)'}
                      </p>
                    )}
                    {tx.match_status === 'suggested' && (
                      <p className="text-xs text-blue-600 mt-0.5">Mogelijk: factuur {tx.suggested_factuur_number} ({tx.suggested_customer || '?'})</p>
                    )}
                  </div>
                  <span className="text-xs text-muted-foreground w-20">{fmtDate(tx.execution_date)}</span>
                  <span className={`font-semibold w-28 text-right ${incoming ? 'text-green-600' : 'text-red-500'}`}>
                    {incoming ? '+' : ''}{fmt(tx.amount)}
                  </span>
                  <Badge variant="outline" className={badge.cls}>{badge.label}</Badge>
                  <div className="flex gap-1">
                    {tx.match_status === 'suggested' && (
                      <Button size="sm" variant="outline" className="h-7 text-xs"
                        onClick={() => doMatch(tx, tx.suggested_factuur_id, tx.suggested_collection)}
                        data-testid="ponto-accept-suggestion-btn">
                        <Link2 className="w-3 h-3 mr-1" /> Koppel
                      </Button>
                    )}
                    {['unmatched', 'suggested', 'pending'].includes(tx.match_status) && incoming && (
                      <>
                        <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => openMatchDialog(tx)} data-testid="ponto-manual-match-btn">
                          Koppelen...
                        </Button>
                        <Button size="sm" variant="ghost" className="h-7 text-xs text-muted-foreground" onClick={() => doIgnore(tx)} data-testid="ponto-ignore-btn">
                          <EyeOff className="w-3 h-3" />
                        </Button>
                      </>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      <Dialog open={!!matchTx} onOpenChange={(o) => !o && setMatchTx(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Koppel transactie aan factuur</DialogTitle>
          </DialogHeader>
          {matchTx && (
            <p className="text-sm text-muted-foreground">
              {fmt(matchTx.amount)} van {matchTx.counterpart_name || 'onbekend'} — {matchTx.remittance || 'geen mededeling'}
            </p>
          )}
          <div className="max-h-80 overflow-y-auto divide-y divide-border/50">
            {openFacturen.length === 0 && <p className="text-sm text-muted-foreground py-4 text-center">Geen openstaande facturen.</p>}
            {openFacturen.map((f) => (
              <button key={`${f.collection}-${f.id}`}
                className="w-full text-left py-2.5 px-2 hover:bg-muted/50 rounded flex items-center justify-between gap-2"
                onClick={() => doMatch(matchTx, f.id, f.collection)}
                data-testid="ponto-factuur-option">
                <span className="min-w-0">
                  <span className="text-sm font-medium">{f.factuur_number}</span>
                  <span className="text-xs text-muted-foreground block truncate">{f.customer_name_snapshot || '-'} · {f.collection === 'facturen' ? 'Reiniging' : 'Installatie'}</span>
                </span>
                <span className="text-sm font-semibold flex-shrink-0">{fmt(f.outstanding)}</span>
              </button>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default BankTransacties;
